import { useCallback, useEffect, useState } from "react";

import isEmpty from "lodash/isEmpty";

import { captureException } from "@/services/AppMonitor";
import { useLazyGetContentFeedQuery } from "@/store/api/user";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { prefetch } from "@/ui/core/Image";

const getImageUrls = (contentFeed: ResourceTheme[]) => {
  return contentFeed
    .flatMap((theme: ResourceTheme) => {
      return theme.resources.map((resource) => resource.imageUrl);
    })
    .filter((url) => !isEmpty(url));
};

interface AppUserContentFeed {
  contentFeed: ResourceTheme[];
  refetchContentFeed: () => Promise<void>;
}
const useAppUserContentFeed = (): AppUserContentFeed => {
  const [contentFeed, setContentFeed] = useState<ResourceTheme[]>([]);

  const { externalId: appUserId, isPatient: isAppUserPatient } =
    useAppUserSelector();

  const [getContentFeed] = useLazyGetContentFeedQuery();

  const fetchContentFeed = useCallback(
    async (shouldPreferCacheValue: boolean) => {
      if (isEmpty(appUserId)) return;
      try {
        const themes = await getContentFeed(
          {
            isPatient: isAppUserPatient,
            isPromoted: true,
            userExternalId: appUserId,
          },
          shouldPreferCacheValue,
        ).unwrap();
        setContentFeed(themes);
      } catch (error) {
        captureException(
          new Error("Failure fetching content feed", { cause: error }),
        );
      }
    },
    [appUserId, getContentFeed, isAppUserPatient],
  );

  const refetchContentFeed = useCallback(async () => {
    await fetchContentFeed(false);
  }, [fetchContentFeed]);

  useEffect(() => {
    void fetchContentFeed(true);
  }, [fetchContentFeed]);

  useEffect(() => {
    if (isEmpty(contentFeed)) return;
    const prefetchImages = async () => {
      const imageUrls = getImageUrls(contentFeed);
      const promises = imageUrls.map((url) => prefetch(url));
      await Promise.allSettled(promises);
    };
    void prefetchImages();
  }, [contentFeed]);

  return {
    contentFeed,
    refetchContentFeed,
  };
};

export default useAppUserContentFeed;
